import { RefObject, useCallback, useRef, useState } from "react";
import { getRectOfNodes, getTransformForBounds } from "reactflow";
import { toCanvas } from "html-to-image";
import jsPDF from "jspdf";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

type DownloadFormat = "png" | "pdf";

type FlowDownloadDropdownProps = {
    reactFlowInstance: any;
    reactFlowWrapperRef: RefObject<HTMLElement>;
    chatbotName?: string;
    flowName?: string;
    className?: string;
};

type StylePatch = { el: HTMLElement | SVGElement; prop: string; prev: string };
type AttrPatch = { el: Element; attr: string; prev: string | null };
declare const bootstrap: any;

const HEADER_HEIGHT = 64;
const EXPORT_PADDING = 120;
const TARGET_PIXEL_RATIO = 2;
const MIN_PIXEL_RATIO = 0.5;
const MAX_EXPORT_SIDE = 12000;
const MAX_EXPORT_PIXELS = 52000000;
const PDF_JPEG_QUALITY = 0.86;
const EDGE_LABEL_NODE_NAMES = ["text", "textpath", "rect"];
const EXPORT_BUTTON_DISABLED_STYLE = {
    "--bs-btn-disabled-bg": "rgba(19, 161, 171, 0.1)",
    "--bs-btn-disabled-color": "#0A595F",
    "--bs-btn-disabled-border-color": "transparent",
    "--bs-btn-disabled-opacity": "1",
} as any;

const sanitizeFilename = (value: string) => {
    return value.trim().replace(/[\\/:*?"<>|]+/g, "-") || "flujo";
};

const drawTextWithEllipsis = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
) => {
    if (ctx.measureText(text).width <= maxWidth) {
        ctx.fillText(text, x, y);
        return;
    }

    const ellipsis = "...";
    let low = 0;
    let high = text.length;

    while (low < high) {
        const mid = Math.ceil((low + high) / 2);

        if (ctx.measureText(`${text.slice(0, mid)}${ellipsis}`).width <= maxWidth) {
            low = mid;
        } else {
            high = mid - 1;
        }
    }

    ctx.fillText(`${text.slice(0, low)}${ellipsis}`, x, y);
};

const getPixelRatio = (width: number, height: number) => {
    const sideLimit = MAX_EXPORT_SIDE / Math.max(width, height);
    const pixelLimit = Math.sqrt(MAX_EXPORT_PIXELS / Math.max(width * height, 1));
    const ratio = Math.min(TARGET_PIXEL_RATIO, sideLimit, pixelLimit);

    return Math.max(MIN_PIXEL_RATIO, ratio);
};

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality?: number) => {
    return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
                return;
            }

            reject(new Error("Canvas export failed"));
        }, type, quality);
    });
};

const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.download = filename;
    anchor.href = url;
    anchor.click();

    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const drawRoundedRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
) => {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
};

function FlowDownloadDropdown({ reactFlowInstance, reactFlowWrapperRef, chatbotName, flowName, className = "" }: FlowDownloadDropdownProps) {
    const { t, i18n } = useTranslation();
    const dropdownButtonRef = useRef<HTMLButtonElement>(null);
    const [isExporting, setIsExporting] = useState(false);

    const downloadFlow = useCallback(async (format: DownloadFormat) => {
        if (!reactFlowInstance || isExporting) return;

        setIsExporting(true);

        try {
            const flowNodes = reactFlowInstance.getNodes();
            if (!flowNodes.length) return;

            const nodesBounds = getRectOfNodes(flowNodes);
            const flowW = Math.round(nodesBounds.width + EXPORT_PADDING * 2);
            const flowH = Math.round(nodesBounds.height + EXPORT_PADDING * 2);
            const totalH = HEADER_HEIGHT + flowH;
            const pixelRatio = getPixelRatio(flowW, totalH);
            const [tx, ty, scale] = getTransformForBounds(nodesBounds, flowW, flowH, 0.5, 2);
            const viewportEl = reactFlowWrapperRef.current?.querySelector(".react-flow__viewport") as HTMLElement | null;

            if (!viewportEl) return;

            const stylePatches: StylePatch[] = [];
            const attrPatches: AttrPatch[] = [];

            const patchStyle = (el: HTMLElement | SVGElement, prop: string, val: string) => {
                stylePatches.push({ el, prop, prev: el.style.getPropertyValue(prop) });
                el.style.setProperty(prop, val, "important");
            };
            const patchAttr = (el: Element, attr: string, val: string) => {
                attrPatches.push({ el, attr, prev: el.getAttribute(attr) });
                el.setAttribute(attr, val);
            };

            const edgeGroups = Array.from(viewportEl.querySelectorAll<SVGGElement>(".react-flow__edge"))
                .reduce((groups, edgeGroup) => {
                    const dataTestId = edgeGroup.getAttribute("data-testid");

                    if (dataTestId?.startsWith("rf__edge-")) {
                        groups.set(dataTestId.replace("rf__edge-", ""), edgeGroup);
                    }

                    return groups;
                }, new Map<string, SVGGElement>());

            const getEdgePath = (edgeId: string) => {
                return (
                    edgeGroups.get(edgeId)?.querySelector<SVGPathElement>(".react-flow__edge-path") ||
                    document.getElementById(edgeId)
                ) as SVGPathElement | null;
            };

            const edgeLabelPositions = new Map<string, { x: number; y: number }>();

            reactFlowInstance.getEdges().forEach((edge) => {
                if (!edge.label) return;

                const labelWrapper = edgeGroups.get(edge.id)?.querySelector<SVGGElement>(".react-flow__edge-textwrapper");
                const labelText = labelWrapper?.querySelector<SVGTextElement>(".react-flow__edge-text");
                const matrix = labelWrapper?.transform?.baseVal?.consolidate()?.matrix;

                if (!labelText || !matrix) return;

                try {
                    const labelBox = labelText.getBBox();
                    if (!labelBox.width || !labelBox.height) return;

                    edgeLabelPositions.set(edge.id, {
                        x: matrix.e + labelBox.x + labelBox.width / 2,
                        y: matrix.f + labelBox.y + labelBox.height / 2,
                    });
                } catch (_) { /* ignore labels that are not measurable yet */ }
            });

            viewportEl.querySelectorAll<SVGPathElement>(".react-flow__edge-path").forEach((el) => {
                patchStyle(el, "stroke", "#475569");
                patchStyle(el, "stroke-width", "2px");
                patchStyle(el, "fill", "none");
                patchAttr(el, "stroke", "#475569");
                patchAttr(el, "stroke-width", "2");
                patchAttr(el, "fill", "none");
            });

            viewportEl.querySelectorAll<SVGElement>(".react-flow__edge-text, .react-flow__edge-textbg, .react-flow__edge text, .react-flow__edge textPath, .react-flow__edge rect").forEach((el) => {
                patchStyle(el, "display", "none");
            });

            const svgSize = String(Math.max(flowW, flowH) / scale + 2000);
            viewportEl.querySelectorAll<SVGSVGElement>("svg").forEach((el) => {
                patchStyle(el, "overflow", "visible");
                patchAttr(el, "width", svgSize);
                patchAttr(el, "height", svgSize);
                patchAttr(el, "overflow", "visible");
            });

            void viewportEl.offsetHeight;

            const restore = () => {
                for (const { el, prop, prev } of stylePatches) {
                    if (prev) el.style.setProperty(prop, prev);
                    else el.style.removeProperty(prop);
                }
                for (const { el, attr, prev } of attrPatches) {
                    if (prev === null) el.removeAttribute(attr);
                    else el.setAttribute(attr, prev);
                }
            };

            const excludeEdgeLabels = (node: Element) => {
                return node instanceof Element && (
                    node.classList.contains("react-flow__edge-text") ||
                    node.classList.contains("react-flow__edge-textbg") ||
                    Boolean(node.closest(".react-flow__edge") && EDGE_LABEL_NODE_NAMES.includes(node.nodeName.toLowerCase()))
                );
            };

            let flowCanvas: HTMLCanvasElement | null = null;

            try {
                flowCanvas = await toCanvas(viewportEl, {
                    backgroundColor: "#f8fafc",
                    width: flowW,
                    height: flowH,
                    pixelRatio,
                    filter: (node: HTMLElement) => !excludeEdgeLabels(node),
                    style: {
                        width: `${flowW}px`,
                        height: `${flowH}px`,
                        transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
                    },
                });
            } finally {
                restore();
            }

            const canvas = document.createElement("canvas");
            canvas.width = Math.round(flowW * pixelRatio);
            canvas.height = Math.round(totalH * pixelRatio);

            const ctx = canvas.getContext("2d");
            if (!ctx || !flowCanvas) return;

            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";
            ctx.scale(pixelRatio, pixelRatio);

            ctx.fillStyle = "#f8fafc";
            ctx.fillRect(0, 0, flowW, totalH);

            ctx.fillStyle = "#0f172a";
            ctx.fillRect(0, 0, flowW, HEADER_HEIGHT);

            const botTitle = (chatbotName || "Chatbot").trim();
            const flowTitle = flowName?.trim() || "";
            const showFlowTitle = Boolean(flowTitle && flowTitle !== botTitle);
            const name = sanitizeFilename(showFlowTitle ? `${botTitle} - ${flowTitle}` : botTitle);
            const locale = i18n.language?.startsWith("en") ? "en-US" : "es-MX";
            const dateStr = new Date().toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" });
            ctx.font = "12px system-ui, -apple-system, sans-serif";
            const dateWidth = ctx.measureText(dateStr).width;
            const headerTextMaxWidth = Math.max(120, flowW - dateWidth - 64);

            if (showFlowTitle) {
                ctx.font = "bold 16px system-ui, -apple-system, sans-serif";
                ctx.fillStyle = "#ffffff";
                drawTextWithEllipsis(ctx, botTitle, 20, 26, headerTextMaxWidth);

                ctx.font = "12px system-ui, -apple-system, sans-serif";
                ctx.fillStyle = "rgba(255,255,255,0.62)";
                drawTextWithEllipsis(ctx, flowTitle, 20, 46, headerTextMaxWidth);
            } else {
                ctx.font = "bold 18px system-ui, -apple-system, sans-serif";
                ctx.fillStyle = "#ffffff";
                drawTextWithEllipsis(ctx, botTitle, 20, HEADER_HEIGHT / 2 + 7, headerTextMaxWidth);
            }

            ctx.font = "12px system-ui, -apple-system, sans-serif";
            ctx.fillStyle = "rgba(255,255,255,0.55)";
            ctx.fillText(dateStr, flowW - ctx.measureText(dateStr).width - 20, HEADER_HEIGHT / 2 + 5);

            ctx.drawImage(flowCanvas, 0, HEADER_HEIGHT, flowW, flowH);

            ctx.save();
            ctx.font = "700 13px system-ui, -apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";

            reactFlowInstance.getEdges().forEach((edge) => {
                if (!edge.label) return;

                try {
                    const labelPosition = edgeLabelPositions.get(edge.id);
                    let labelX = labelPosition?.x;
                    let labelY = labelPosition?.y;

                    if (typeof labelX !== "number" || typeof labelY !== "number") {
                        const pathEl = getEdgePath(edge.id);
                        if (!pathEl) return;

                        const len = pathEl.getTotalLength();
                        if (!len) return;

                        const mid = pathEl.getPointAtLength(len / 2);
                        labelX = mid.x;
                        labelY = mid.y;
                    }

                    const cx = labelX * scale + tx;
                    const cy = labelY * scale + ty + HEADER_HEIGHT;
                    const label = String(edge.label);
                    const textWidth = ctx.measureText(label).width;
                    const pillH = 24;
                    const pillW = textWidth + 22;
                    const pillX = cx - pillW / 2;
                    const pillY = cy - pillH / 2;

                    drawRoundedRect(ctx, pillX, pillY, pillW, pillH, 7);
                    ctx.fillStyle = "rgba(255,255,255,0.96)";
                    ctx.fill();
                    ctx.strokeStyle = "#cbd5e1";
                    ctx.lineWidth = 1;
                    ctx.stroke();

                    ctx.fillStyle = "#0f172a";
                    ctx.fillText(label, cx, cy);
                } catch (_) { /* ignore offscreen/disconnected edges */ }
            });

            ctx.restore();

            if (format === "png") {
                const blob = await canvasToBlob(canvas, "image/png");
                downloadBlob(blob, `${name}.png`);
            } else {
                const pdf = new jsPDF({
                    orientation: flowW > totalH ? "landscape" : "portrait",
                    unit: "px",
                    format: [flowW, totalH],
                    compress: true,
                });
                const jpegUrl = canvas.toDataURL("image/jpeg", PDF_JPEG_QUALITY);

                pdf.addImage(jpegUrl, "JPEG", 0, 0, flowW, totalH, undefined, "FAST");
                pdf.save(`${name}.pdf`);
            }

            flowCanvas.width = 0;
            flowCanvas.height = 0;
            canvas.width = 0;
            canvas.height = 0;
        } catch (error) {
            console.error("FlowDownloadDropdown.downloadFlow.error::", error);
            Swal.fire(t("platform.chatbot.download-error-title"), t("platform.chatbot.download-error-text"), "error");
        } finally {
            setIsExporting(false);
        }
    }, [chatbotName, i18n.language, isExporting, reactFlowInstance, reactFlowWrapperRef, t]);

    const handleDownloadClick = useCallback((format: DownloadFormat) => {
        if (dropdownButtonRef.current) {
            bootstrap.Dropdown.getOrCreateInstance(dropdownButtonRef.current).hide();
            dropdownButtonRef.current.blur();
        }

        downloadFlow(format);
    }, [downloadFlow]);

    return (
        <div className={`dropdown ${className}`}>
            <button
                ref={dropdownButtonRef}
                id="flowDownloadDropdown"
                type="button"
                className="btn bg-soft-info-alternative dropdown-toggle text-white-dark"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                aria-label={t("components.custom-table.export")}
                title={t("components.custom-table.export")}
                disabled={isExporting || !reactFlowInstance}
                style={EXPORT_BUTTON_DISABLED_STYLE}
            >
                {isExporting ? (
                    <span className="spinner-border spinner-border-sm me-2 text-primary" role="status" aria-hidden="true" />
                ) : (
                    <i className="bi-download me-2 text-primary fw-bolder fs-5" />
                )}
                {isExporting ? t("platform.chatbot.download-exporting") : t("components.custom-table.export")}
            </button>
            <div className="dropdown-menu dropdown-menu-sm-end" aria-labelledby="flowDownloadDropdown">
                <button className="dropdown-item" onClick={() => handleDownloadClick("png")} disabled={isExporting}>
                    <img className="avatar avatar-xss avatar-4x3 me-2" src="/assets/svg/components/placeholder-img-format.svg" alt="PNG" />
                    {t("platform.chatbot.download-png")}
                </button>
                <button className="dropdown-item" onClick={() => handleDownloadClick("pdf")} disabled={isExporting}>
                    <img className="avatar avatar-xss avatar-4x3 me-2" src="/assets/svg/brands/pdf-icon.svg" alt="PDF" />
                    {t("platform.chatbot.download-pdf")}
                </button>
            </div>
        </div>
    );
}

export default FlowDownloadDropdown;
